/**
 * JSON and structured-data helpers (W1-01 core-time-math): deep freezing (dev checks on immutable records),
 * a canonical stable stringify for hashing and cache keys, JSON equality / cloning / validation, and a fast
 * non-cryptographic string hash. (Lossless JSON with raw number lexemes lives in formats/json, W1-07a.)
 */

import type { Json } from './types';

/** True for objects created by `{}` / `Object.create(null)` / JSON.parse (not arrays, maps, class instances). */
export function isPlainObject(x: unknown): x is Readonly<Record<string, unknown>> {
  if (x === null || typeof x !== 'object' || Array.isArray(x)) return false;
  const proto: unknown = Object.getPrototypeOf(x);
  return proto === Object.prototype || proto === null;
}

/**
 * Defines an own enumerable data property. Unlike `obj[key] = value` this is safe for keys such as "__proto__"
 * that JSON.parse produces as ordinary members.
 */
export function setOwn<T>(obj: Record<string, T>, key: string, value: T): void {
  Object.defineProperty(obj, key, { value, enumerable: true, writable: true, configurable: true });
}

// ---------------------------------------------------------------------------------------------------------------
// Freezing
// ---------------------------------------------------------------------------------------------------------------

function freezeRec(x: unknown, seen: WeakSet<object>): void {
  if (x === null || typeof x !== 'object') return;
  if (seen.has(x)) return;
  seen.add(x);
  // Typed arrays and DataViews cannot be frozen while they have elements; buffers have no own properties.
  if (ArrayBuffer.isView(x) || x instanceof ArrayBuffer) return;
  if (x instanceof Map) {
    for (const [k, v] of x as Map<unknown, unknown>) {
      freezeRec(k, seen);
      freezeRec(v, seen);
    }
  } else if (x instanceof Set) {
    for (const v of x as Set<unknown>) freezeRec(v, seen);
  }
  Object.freeze(x);
  for (const key of Reflect.ownKeys(x)) {
    freezeRec((x as Record<PropertyKey, unknown>)[key], seen);
  }
}

/**
 * Recursively freezes plain objects and arrays (and the contents of Maps / Sets; the Map / Set objects
 * themselves stay mutable through their methods, as JS allows). Returns its argument. Used in development
 * builds and tests to catch accidental mutation of immutable records.
 */
export function deepFreeze<T>(x: T): T {
  freezeRec(x, new WeakSet());
  return x;
}

/** True when x and everything reachable through own properties is frozen (typed arrays excepted). */
export function isDeepFrozen(x: unknown): boolean {
  const seen = new WeakSet();
  const check = (v: unknown): boolean => {
    if (v === null || typeof v !== 'object') return true;
    if (seen.has(v)) return true;
    seen.add(v);
    if (ArrayBuffer.isView(v) || v instanceof ArrayBuffer) return true;
    if (!Object.isFrozen(v)) return false;
    if (v instanceof Map) {
      for (const [k, val] of v as Map<unknown, unknown>) if (!check(k) || !check(val)) return false;
    } else if (v instanceof Set) {
      for (const val of v as Set<unknown>) if (!check(val)) return false;
    }
    for (const key of Reflect.ownKeys(v)) {
      if (!check((v as Record<PropertyKey, unknown>)[key])) return false;
    }
    return true;
  };
  return check(x);
}

// ---------------------------------------------------------------------------------------------------------------
// Stable stringify
// ---------------------------------------------------------------------------------------------------------------

function isSkippable(v: unknown): boolean {
  return v === undefined || typeof v === 'function' || typeof v === 'symbol';
}

function compareStrings(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function stringifyRec(value: unknown, key: string, stack: Set<object>): string {
  let v = value;
  if (v !== null && typeof v === 'object' && 'toJSON' in v && typeof v.toJSON === 'function') {
    v = (v.toJSON as (k: string) => unknown).call(v, key);
  }
  if (v === null || isSkippable(v)) return 'null';
  switch (typeof v) {
    case 'boolean':
      return v ? 'true' : 'false';
    case 'number':
      return Number.isFinite(v) ? JSON.stringify(v) : 'null';
    case 'string':
      return JSON.stringify(v);
    case 'bigint':
      throw new TypeError('stableStringify: BigInt values are not serialisable');
    case 'object':
      break;
    default:
      return 'null';
  }
  const obj = v;
  if (stack.has(obj)) throw new TypeError('stableStringify: cyclic structure');
  stack.add(obj);
  try {
    if (Array.isArray(obj)) {
      const parts = (obj as readonly unknown[]).map((item, i) => stringifyRec(item, String(i), stack));
      return `[${parts.join(',')}]`;
    }
    if (ArrayBuffer.isView(obj) && !(obj instanceof DataView)) {
      const items = Array.from(obj as unknown as ArrayLike<number>, (n) => stringifyRec(n, '', stack));
      return `[${items.join(',')}]`;
    }
    if (obj instanceof Map) {
      const entries = Array.from(obj as Map<unknown, unknown>, ([k, val]) => [
        stringifyRec(k, '', stack),
        stringifyRec(val, '', stack),
      ]);
      entries.sort((a, b) => compareStrings(a[0] ?? '', b[0] ?? ''));
      return `{"$map":[${entries.map(([k, val]) => `[${k ?? 'null'},${val ?? 'null'}]`).join(',')}]}`;
    }
    if (obj instanceof Set) {
      const items = Array.from(obj as Set<unknown>, (item) => stringifyRec(item, '', stack));
      items.sort(compareStrings);
      return `{"$set":[${items.join(',')}]}`;
    }
    const rec = obj as Record<string, unknown>;
    const keys = Object.keys(rec).sort(compareStrings);
    const parts: string[] = [];
    for (const k of keys) {
      const item = rec[k];
      if (isSkippable(item)) continue;
      parts.push(`${JSON.stringify(k)}:${stringifyRec(item, k, stack)}`);
    }
    return `{${parts.join(',')}}`;
  } finally {
    stack.delete(obj);
  }
}

/**
 * Canonical JSON text for hashing and cache keys: JSON.stringify semantics (toJSON, non-finite numbers and
 * unserialisable values as null, undefined object members omitted) with object keys sorted by code unit.
 * Maps serialise as `{"$map":[[k,v],...]}` and Sets as `{"$set":[...]}` (entries sorted), typed arrays as number
 * arrays, so documents built from Map tables hash by content. Throws TypeError on cycles and BigInts.
 */
export function stableStringify(value: unknown): string {
  return stringifyRec(value, '', new Set());
}

// ---------------------------------------------------------------------------------------------------------------
// JSON values
// ---------------------------------------------------------------------------------------------------------------

/** Deep equality of JSON values: arrays element-wise, objects by key set (key order ignored). */
export function jsonEquals(a: Json, b: Json): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  const aArr = Array.isArray(a);
  if (aArr !== Array.isArray(b)) return false;
  if (aArr) {
    const x = a as readonly Json[];
    const y = b as readonly Json[];
    if (x.length !== y.length) return false;
    for (let i = 0; i < x.length; i++) if (!jsonEquals(x[i] ?? null, y[i] ?? null)) return false;
    return true;
  }
  const x = a as { readonly [k: string]: Json };
  const y = b as { readonly [k: string]: Json };
  const xk = Object.keys(x);
  if (xk.length !== Object.keys(y).length) return false;
  for (const k of xk) {
    if (!Object.prototype.hasOwnProperty.call(y, k)) return false;
    if (!jsonEquals(x[k] ?? null, y[k] ?? null)) return false;
  }
  return true;
}

/** Deep copy of a JSON value (plain objects and arrays only). */
export function cloneJson<T extends Json>(x: T): T {
  if (x === null || typeof x !== 'object') return x;
  if (Array.isArray(x)) return (x as readonly Json[]).map((item) => cloneJson(item)) as unknown as T;
  const out: Record<string, Json> = {};
  for (const [k, v] of Object.entries(x as { readonly [k: string]: Json })) setOwn(out, k, cloneJson(v));
  return out as T;
}

/**
 * Validates that x is a JSON value: null, boolean, finite number, string, arrays and plain objects of JSON
 * values, without cycles.
 */
export function isJson(x: unknown): x is Json {
  const stack = new Set<object>();
  const check = (v: unknown): boolean => {
    if (v === null) return true;
    switch (typeof v) {
      case 'boolean':
      case 'string':
        return true;
      case 'number':
        return Number.isFinite(v);
      case 'object':
        break;
      default:
        return false;
    }
    const obj = v;
    if (stack.has(obj)) return false;
    if (!Array.isArray(obj) && !isPlainObject(obj)) return false;
    stack.add(obj);
    try {
      if (Array.isArray(obj)) return (obj as readonly unknown[]).every(check);
      return Object.values(obj).every(check);
    } finally {
      stack.delete(obj);
    }
  };
  return check(x);
}

/**
 * Structured clone (Maps, Sets, typed arrays, Dates, cycles) of data that crosses a worker boundary or is
 * snapshotted. Functions and class prototypes are not preserved (they never appear in documents).
 */
export function deepClone<T>(x: T): T {
  return structuredClone(x);
}

// ---------------------------------------------------------------------------------------------------------------
// Hashing
// ---------------------------------------------------------------------------------------------------------------

/** cyrb53: fast 53-bit non-cryptographic string hash (cache keys, change detection). Not for security. */
export function hash53(s: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/** Content hash of a value through `stableStringify` (base-36 string). */
export function hashValue(x: unknown): string {
  return hash53(stableStringify(x)).toString(36);
}
